import { createPortal } from 'react-dom';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { overlayCardClass, overlayCardSizes, overlayScrimModalClass } from '@/lib/overlayStyles';

/**
 * Shows the invite link so it can be handed over by hand. There is no mailer, so this
 * dialog is the only delivery mechanism — it stays open until it is dismissed.
 */
export function InviteLinkPanel({ inviteUrl, onClose }: { inviteUrl: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is blocked outside a secure context; the link is selectable anyway.
      toast.error('Could not copy — select the link and copy it manually.');
    }
  };

  return createPortal(
    <div className={cn(overlayScrimModalClass)} onClick={onClose} role="presentation">
      <div
        className={cn(overlayCardClass, overlayCardSizes.lg)}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col gap-5 p-6 text-white">
          <header className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Invitation created</p>
            <p className="text-sm text-white/55">
              Send this link to the invitee. It is not e-mailed automatically, and it is shown
              again on the invitations list if you lose it.
            </p>
          </header>

          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-content-highlight/40 bg-content-dark/60 p-3">
            <code className="min-w-0 flex-1 break-all text-xs text-primary-200">{inviteUrl}</code>
            <Button type="button" variant="ghost-light" size="sm" onClick={copy}>
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
